/* eslint-disable react/prop-types */
import { useState, useEffect } from 'react';
import Input from '@components/forms/Input';
import { Link, useNavigate } from 'react-router-dom';
import Button from '@components/forms/Button';
import { ReactComponent as MobileIcon } from '@assets/icons/mobile_icon.svg';
import { ReactComponent as ErrorIcon } from '@assets/icons/info_error_light.svg';
import { ReactComponent as ArrowRight } from '@assets/icons/arrow_grey_right_icon.svg';
import { ReactComponent as InfoBigIcon } from '@assets/icons/info_big.svg';
import { ReactComponent as ContentCopyIcon } from '@assets/icons/content_copy.svg';
import appStoreImage from '@assets/images/app-store.png';
import googlePlayImage from '@assets/images/google-play-store.png';
import {
  post,
  AUTH_LOGIN,
  CURRENT_USER,
  refreshAccessToken,
  GET_LENDER_ORGANISATION,
  MFA_SETUP,
  VERIFY_MFA_SETUP,
  MFA_LOGIN
} from '@services';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import clsx from 'clsx';
import Loader from '@components/widgets/Loader';
import { useAuthStore } from '@stores';
import { parseISO } from 'date-fns';
import LenderSelect from './components/LenderSelect';
import { needsLenderOnboarding } from '@utils/userUtils';
import { clearLocalStoragePreservingPreferences } from '@utils';


/** Lender enquiries: same contact destination as other auth flows */
const AUTHENTIK8_LENDER_CONTACT_URL =
  'https://www.valeur.io/authentik8';

const loginFormSchema = yup
  .object({
    email: yup.string().email().required(),
    password: yup.string().required(),
    rememberFor30Days: yup.boolean(),
    mfaCode: yup.string(),
    mfaSetupCode: yup.string()
  })
  .required();
const Login = () => {
  const {
    register,
    watch,
    setValue,
    getValues,
    setError,
    setFocus,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberFor30Days: false,
      mfaCode: '',
      mfaSetupCode: ''
    }
  });
  const {
    user,
    setUser,
    setSelectedOrganisationId,
    setorganisationConfig,
    setLenders,
    setBrokers,
    resetAuth
  } = useAuthStore();
  const navigate = useNavigate();
  const { email, password } = watch();
  const [showMFA, setShowMFA] = useState(1);
  const [isLoading, setLoading] = useState(false);
  const [secretCode, setSecretCode] = useState('');
  const [rememberFor30Days, setRememberFor30Days] = useState(false);
  const [svg, setSVG] = useState(
    window.atob(localStorage.getItem('qrCodeAsBase64') || '')
  );
  const handleRememberFor30DaysChange = () => {
    setRememberFor30Days(!rememberFor30Days);
  };
  const resetStoredSession = () => {
    resetAuth();
    clearLocalStoragePreservingPreferences();
    sessionStorage.clear();
    setSecretCode('');
    setSVG('');
  };
  const handleSetup = () => {
    setShowMFA(3);
    setUpCurrentUser(false);
  };

  useEffect(() => {
    if (showMFA === 4) {
      setFocus('mfaCode');
    }
  }, [showMFA, setFocus]);

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    const apiExpiry = localStorage.getItem('expiry');
    const longExpiry = localStorage.getItem('longExpiry');
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    const now = new Date();
    const isTokenExpired = (expiry: string) => {
      const expiryDate = Number(expiry) || parseISO(expiry).getTime();
      return now.getTime() > expiryDate;
    };
    const clearStoredTokens = () => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('expiry');
      localStorage.removeItem('longExpiry');
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('refreshToken');
    };
    const restoreRememberedSession = async () => {
      try {
        await refreshAccessToken();
        navigate('/'); // Redirect to home page after successful refresh
      } catch {
        clearStoredTokens();
      }
    };

    if (
      rememberMe &&
      longExpiry &&
      now.getTime() < Number(longExpiry) &&
      apiExpiry
    ) {
      if (isTokenExpired(apiExpiry)) {
        // If "Remember Me" is valid but API token is expired, try to refresh the token
        restoreRememberedSession();
      } else {
        // "Remember Me" is valid and API token is not expired
        navigate('/');
      }
    } else if (accessToken && apiExpiry && !isTokenExpired(apiExpiry)) {
      // If only the short-term API token is valid, also redirect to the home page
      // navigate('/');
    } else {
      // If all tokens are invalid or expired, clear local storage and stay on the login page
      clearStoredTokens();
    }
  }, []);

  const setUpCurrentUser = (redirect: boolean) => {
    post(CURRENT_USER, {}).then((resp) => {
      if (resp.user) {
        setUser(resp.user);
        localStorage.setItem('userId', resp.user.userId);
        localStorage.setItem('lastLogin', resp.user.lastLogin);
        localStorage.setItem('email', resp.user.email);
        localStorage.setItem('firstName', resp.user.firstName);
        localStorage.setItem('lastName', resp.user?.lastName || '');
      }
      const userId = resp.user.userId;
      if (resp.verifiers) {
        localStorage.setItem(
          `verifiers_${userId}`,
          JSON.stringify(resp.verifiers)
        );
      }
      if (resp.system) {
        localStorage.setItem(`roles_${userId}`, resp.system.roles);
      }

      const brokers = resp.brokers ?? [];
      localStorage.setItem(`brokers_${userId}`, JSON.stringify(brokers));
      setBrokers(brokers);

      if (resp.lenderBrokers && resp.lenderBrokers.length > 0) {
        localStorage.setItem(
          `lenderBrokers_${userId}`,
          JSON.stringify(resp.lenderBrokers)
        );
      }

      if (resp.lenders && resp.lenders.length > 0) {
        setLenders(resp.lenders);
        setLoading(false);
        if (resp.lenders.length === 1) {
          localStorage.setItem(
            `lenders_${userId}`,
            JSON.stringify(resp.lenders)
          );

          post(
            GET_LENDER_ORGANISATION,
            { organisationId: resp.lenders[0].organisationId },
            false
          )
            .then((response) => {
              if (response && response.lender) {
                const data = response.lender;
                setorganisationConfig({
                  ...data,
                  hasInspections: data.hasInspections || true,
                  hasAssetVerifications: data.hasAssetVerifications || true
                });
                setSelectedOrganisationId(data.organisationId);
              }
            })
            .catch((e) => {
              console.error(e);
            })
            .finally(() => {
              if (redirect) {
                navigate('/');
              }
            });
        } else if (resp.lenders.length > 1) {
          if (redirect) {
            setShowMFA(5);
          }
        } else {
          if (redirect) {
            navigate('/');
          }
        }
      } else if (resp.lenderBrokers && resp.lenderBrokers.length > 0) {
        localStorage.setItem(
          `lenderBrokers_${userId}`,
          JSON.stringify(resp.lenderBrokers)
        );
        setLoading(false);
        if (redirect) {
          navigate('/');
        }
      } else {
        // Broker user only — send brand-new brokers to lender onboarding.
        setLoading(false);
        if (needsLenderOnboarding(resp.lenders, resp.lenderBrokers)) {
          navigate('/welcome/lenders');
        } else {
          navigate('/');
        }
      }
    });
  };
  const persistLoginSession = (response: {
    accessToken: string;
    refreshToken: string;
    expiry: string;
    clientId?: string;
    id: string;
  }) => {
    setUser(response);
    if (response.clientId) {
      localStorage.setItem('clientId', response.clientId);
    }
    localStorage.setItem('accessToken', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem('expiry', response.expiry);
    localStorage.setItem('id', response.id);
  };

  const requiresMfaSetup = (response: {
    requiresMfaSetup?: boolean;
    challengeName?: string;
  }) =>
    response.requiresMfaSetup === true ||
    response.challengeName === 'MFA_SETUP';

  const onSubmit = () => {
    setLoading(true);
    post(AUTH_LOGIN, { email, password }, false)
      .then(async (response) => {
        const {
          accessToken,
          refreshToken,
          expiry,
          clientId,
          id,
          challengeName,
          session
        } = response;
        setLoading(false);
        resetStoredSession();
        localStorage.setItem('email', email);
        if (rememberFor30Days) {
          localStorage.setItem('rememberFor30Days', 'true');
        } else {
          localStorage.removeItem('rememberFor30Days');
        }
        //save accessToken
        if (session && challengeName && challengeName !== 'MFA_SETUP') {
          localStorage.setItem('challengeName', challengeName);
          localStorage.setItem('session', session);
          setShowMFA(4);
        } else if (accessToken && id) {
          persistLoginSession({
            accessToken,
            refreshToken,
            expiry,
            clientId,
            id
          });

          if (requiresMfaSetup(response)) {
            setShowMFA(2);
            post(MFA_SETUP, {}, false).then((response) => {
              if (response.secretCode && response.qrCodeAsBase64) {
                localStorage.setItem('secretCode', response.secretCode);
                setSecretCode(response.secretCode);
                localStorage.setItem('qrCodeAsBase64', response.qrCodeAsBase64);
                setSVG(window.atob(response.qrCodeAsBase64));
              }
            });
          } else {
            setLoading(true);
            setUpCurrentUser(true);
          }
        }
      })
      .catch(() => {
        setLoading(false);
        setError('password', {
          type: 'custom',
          message: 'The account or password is not correct.'
        });
      });
  };
  const handleMailChange = (e: any) => {
    const value = e.target.value;
    setValue('email', value);
  };
  const handlePwdChange = (e: any) => {
    const value = e.target.value;
    setValue('password', value);
  };
  const handleVerify = (e: any) => {
    e.preventDefault();
    const email = getValues('email');
    const session = localStorage.getItem('session');
    const mfaCode = getValues('mfaCode');
    setLoading(false);
    post(MFA_LOGIN, { email, mfaToken: mfaCode, session }, false)
      .then((response) => {
        setLoading(true);
        if (response) {
          localStorage.setItem('accessToken', response.accessToken);
          localStorage.setItem('refreshToken', response.refreshToken);
          localStorage.setItem('expiry', response.expiry);
          if (response.clientId) {
            localStorage.setItem('clientId', response.clientId);
          }
          setUpCurrentUser(true);
        }
      })
      .catch(() => {
        setLoading(false);
        setError('mfaCode', {
          type: 'custom',
          message: 'The authentication code is invalid, please try again.'
        });
      });
  };
  const handleSelectLender = (lender: any) => {
    localStorage.setItem(`lenders_${user.userId}`, JSON.stringify([lender]));
    setSelectedOrganisationId(lender.organisationId);
    navigate('/');
  };
  if (isLoading) return <Loader />;
  switch (showMFA) {
    case 1:
      return (
        <div className="mx-auto w-full md:w-[520px] bg-white px-8 gap-6 md:px-10 py-12 rounded-xl shadow-paper relative">
          <form
            className="flex flex-col gap-6"
            onSubmit={handleSubmit(onSubmit)}>
            <div className="gap-2 flex flex-col">
              <h3 className="text-2xl text-grey-900 font-semibold text-center">
                Access your Authentik8 account
              </h3>
              <p className="text-center text-grey-600 text-sm md:text-base leading-relaxed">
                Sign in to continue. Brokers can sign up online, or lenders can
                contact us to get started.
              </p>
            </div>
            <div className="flex flex-col gap-5">
              <label className="flex flex-col">
                <span className="text-sm font-medium text-grey-700">Email</span>
                <Input
                  type="email"
                  autoComplete="username"
                  {...register(`email`)}
                  placeholder="Enter your email"
                  icon={errors.email && <ErrorIcon />}
                  value={email}
                  onChange={handleMailChange}
                />
                {errors.email && (
                  <span className="text-error-500 text-2sm mt-[6px]">
                    {errors.email.message}
                  </span>
                )}
              </label>
              <div className="flex flex-col gap-0">
                <label className="flex flex-col">
                  <span className="text-sm font-medium text-grey-700">
                    Password
                  </span>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    {...register(`password`)}
                    placeholder="Enter your password"
                    icon={errors.password && <ErrorIcon />}
                    value={password}
                    onChange={handlePwdChange}
                  />
                  {errors.password && (
                    <span className="text-error-500 text-2sm mt-[6px]">
                      {errors.password.message}
                    </span>
                  )}
                </label>
                <div className="flex justify-end items-center mt-2">
                  <Link
                    to="/forgot-password"
                    className="text-blue-600 font-semibold text-sm">
                    Forgot password
                  </Link>
                </div>
              </div>
            </div>
            <div className="flex flex-col justify-center mt-4">
              <Button
                type="submit"
                className="w-full bg-primary text-white text-md font-semibold normal">
                Sign in
              </Button>
            </div>
          </form>

          <div className="mt-8 flex flex-col gap-5">
            <div className="relative flex items-center gap-3">
              <div className="h-px flex-1 bg-grey-200" aria-hidden />
              <span className="shrink-0 text-sm font-medium text-grey-500">
                Or
              </span>
              <div className="h-px flex-1 bg-grey-200" aria-hidden />
            </div>
            <p className="text-center text-2xl text-grey-900 font-semibold relative top-[10px]">
              New to Authentik8?
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:gap-3">
              <Link
                to="/broker/access-apply"
                className="flex h-[44px] min-h-[44px] flex-1 items-center justify-center rounded-lg border border-grey-300 bg-white px-4 text-center text-sm font-semibold text-grey-900 shadow-none transition-colors hover:bg-grey-50">
                Sign up as a broker
              </Link>
              <a
                href={AUTHENTIK8_LENDER_CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-[44px] min-h-[44px] flex-1 items-center justify-center rounded-lg border border-grey-300 bg-white px-4 text-center text-sm font-semibold text-grey-900 shadow-none transition-colors hover:bg-grey-50">
                Lender? Get in touch
              </a>
            </div>
          </div>
        </div>
      );
    case 2:
      return (
        <div className="mx-auto bg-white w-full md:w-[1129px] px-8 gap-8 md:px-10 py-12 rounded-xl shadow-paper relative flex flex-cols">
          <TwoFactorAuth
            secretCode={secretCode}
            svg={svg}
            redirect={handleSetup}
          />
        </div>
      );
    case 3:
      return (
        <div className="mx-auto w-full md:w-[500px] bg-white px-8 md:px-10 py-12 rounded-xl shadow-paper relative gap-8">
          <div className="flex flex-col gap-2">
            <h3 className="text-3xl font-semibold text-gray-800 text-black">
              Two-factor authentication is enabled for your account
            </h3>
            <p className="text-grey-700">
              We’ll ask for an authentication code any time you log in with your
              email and password
            </p>
          </div>
          <div className="flex mt-8">
            <button
              className="px-4 py-2 bg-primary text-white text-base font-semibold rounded-md shadow-sm"
              onClick={() => navigate('/')}>
              Continue
            </button>
          </div>
        </div>
      );
    case 4:
      return (
        <div className="mx-auto w-full md:w-[500px] bg-white px-[40px] py-[50px] rounded-xl shadow-paper relative flex flex-col gap-8">
          <div className="flex justify-center items-center">
            <MobileIcon />
          </div>
          <div className="flex flex-col gap-6">
            <h3 className="text-3xl font-semibold text-gray-800 text-black">
              Enter authentication code
            </h3>
            <p className="text-grey-700">
              Enter the 6-digit code generated by your authentication app.
            </p>
          </div>
          <form
            onSubmit={handleVerify}
            className="flex items-center flex-col md:flex-row justify-center md:justify-between gap-4">
            <div className="w-full">
              <Input
                className={clsx(
                  'text-gray-600 shadow-none ring-none border focus:!border-none w-full rounded-lg max-w-[300px]',
                  errors.mfaCode && '!border-error-300 '
                )}
                autoComplete="off"
                icon={errors.mfaCode && <ErrorIcon />}
                placeholder=""
                pattern="[0-9]*"
                inputMode="numeric"
                maxLength={6}
                onInput={(e: any) => {
                  e.target.value = e.target.value.replace(/[^0-9]/g, '');
                }}
                {...register(`mfaCode`)}
              />
              {errors.mfaCode && (
                <span className="text-error-500 text-2sm mt-[6px]">
                  {errors.mfaCode?.message?.toString()}
                </span>
              )}
            </div>
            <Button
              type="submit"
              className="px-4 py-2 bg-primary text-white text-base font-semibold w-full md:w-auto rounded-md shadow-sm items-start self-start !overflow-visible">
              Continue
            </Button>
          </form>
        </div>
      );
    case 5:
      return <LenderSelect />;
    default:
      return null;
  }
};
type Props = {
  svg: string;
  secretCode: string;
  redirect: () => void;
};
const TwoFactorAuth: React.FC<Props> = ({ svg, secretCode, redirect }) => {
  const {
    register,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm();
  const { mfaSetupToken } = watch();

  // Detect if user is on iOS or Android
  const getUserPlatform = () => {
    const userAgent =
      navigator.userAgent || navigator.vendor || (window as any).opera;

    // iOS detection
    if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
      return 'ios';
    }

    // Android detection
    if (/android/i.test(userAgent)) {
      return 'android';
    }

    // Default to showing both options
    return 'both';
  };

  const platform = getUserPlatform();
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopyCode = async () => {
    if (!secretCode) return;

    try {
      // Check if clipboard API is available
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(secretCode);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } else {
        // Fallback for older browsers or Safari
        const textArea = document.createElement('textarea');
        textArea.value = secretCode;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand('copy');
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
          console.error('Fallback copy failed:', err);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (err) {
      console.error('Copy failed:', err);
      // Try fallback method
      const textArea = document.createElement('textarea');
      textArea.value = secretCode;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand('copy');
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (fallbackErr) {
        console.error('All copy methods failed:', fallbackErr);
      } finally {
        document.body.removeChild(textArea);
      }
    }
  };

  const handleSetupCodeChange = (e: any) => {
    clearErrors('mfaSetupToken');
    const value = e.target.value;
    setValue('mfaSetupToken', value);
  };
  const handleSetup = (e: any) => {
    e.preventDefault();
    post(VERIFY_MFA_SETUP, { mfaToken: mfaSetupToken }, false)
      .then(() => {
        redirect();
      })
      .catch(() => {
        setError('mfaSetupToken', {
          type: 'custom',
          message: 'The 6-digit code is invalid, please try again.'
        });
      });
  };
  return (
    <>
      <div className="flex flex-col space-y-6 w-full">
        <div className="text-gray-600 flex w-full">
          <ol className="flex flex-col gap-6 text-grey-700 w-full">
            <li className="pb-6 mb-2 md:mb-0 w-full">
              <h3 className="text-[30px] md:text-3xl font-semibold text-gray-900 text-black">
                Two-factor authentication is <br className="hidden md:block" />
                required for your account
              </h3>
            </li>
            <li className="flex flex-col md:flex-row gap-6 pb-4 border-b whitespace-wrap w-full">
              <span className="text-grey-900 whitespace-nowrap font-semibold">
                Step 1
              </span>
              <div className="flex flex-col gap-4">
                <span className="hidden md:block">
                  If you already have{' '}
                  <span className="font-semibold">Google Authenticator</span> or{' '}
                  <span className="font-semibold">Microsoft Authenticator</span>{' '}
                  on your phone, open it now. If not, please install one of
                  these apps from the official App Store (Apple App Store or
                  Google Play).
                </span>
                <span className="block md:hidden">
                  If you already have{' '}
                  <span className="font-semibold">Google Authenticator</span> or{' '}
                  <span className="font-semibold">Microsoft Authenticator</span>{' '}
                  on your phone, open it now. If not, please install one of
                  these apps from the official App Store.
                </span>

                {/* Desktop info box */}
                <div className="hidden md:flex gap-4 items-center bg-[#eff8ff] p-4 rounded-lg">
                  <InfoBigIcon className="flex-shrink-0 w-8 h-8" />
                  <span className="text-[16px] text-grey-700">
                    Make sure you are downloading the official app published by{' '}
                    <span className="font-semibold">Google LLC</span> or{' '}
                    <span className="font-semibold">Microsoft Corporation</span>
                    , not a third-party alternative.
                  </span>
                </div>

                {/* Mobile app store links */}
                <div className="block md:hidden mt-6">
                  <div className="bg-white rounded-lg">
                    <div className="flex flex-col gap-6">
                      {/* Google Authenticator */}
                      <div className="pb-3">
                        <h4 className="font-semibold mb-2">
                          Google Authenticator
                        </h4>
                        <div className="flex gap-2">
                          {(platform === 'ios' || platform === 'both') && (
                            <a
                              href="https://apps.apple.com/au/app/google-authenticator/id388497605"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block">
                              <img
                                src={appStoreImage}
                                alt="Download on App Store"
                                className="h-10"
                              />
                            </a>
                          )}
                          {(platform === 'android' || platform === 'both') && (
                            <a
                              href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&pcampaignid=web_share"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block">
                              <img
                                src={googlePlayImage}
                                alt="Get it on Google Play"
                                className="h-10"
                              />
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Microsoft Authenticator */}
                      <div>
                        <h4 className="font-semibold text-sm mb-2">
                          Microsoft Authenticator
                        </h4>
                        <div className="flex gap-2">
                          {(platform === 'ios' || platform === 'both') && (
                            <a
                              href="https://apps.apple.com/au/app/microsoft-authenticator/id983156458"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block">
                              <img
                                src={appStoreImage}
                                alt="Download on App Store"
                                className="h-10"
                              />
                            </a>
                          )}
                          {(platform === 'android' || platform === 'both') && (
                            <a
                              href="https://play.google.com/store/apps/details?id=com.azure.authenticator&pcampaignid=web_share"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block">
                              <img
                                src={googlePlayImage}
                                alt="Get it on Google Play"
                                className="h-10"
                              />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </li>
            <li className="flex flex-col md:flex-row gap-6 pb-4 border-b whitespace-wrap w-full pt-6">
              <span className="text-grey-900 whitespace-nowrap font-semibold">
                Step 2
              </span>
              <div className="text-grey-700">
                <span className="hidden lg:block">
                  Using the app, scan the QR code on the right or manually enter
                  this key:
                </span>
                <span className="block lg:hidden">
                  Using the app, manually enter this key:
                </span>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    onClick={handleCopyCode}
                    className="lg:hidden p-2 hover:bg-gray-100 active:bg-gray-200 rounded transition-colors flex-shrink-0 touch-manipulation"
                    title="Copy code">
                    <ContentCopyIcon className="w-10 h-10 text-gray-600" />
                  </button>
                  <span className="block break-words break-all font-semibold">
                    {secretCode}.
                  </span>
                </div>
                {copySuccess && (
                  <span className="text-sm text-green-600 mt-1 block lg:hidden">
                    Copied!
                  </span>
                )}
              </div>
            </li>
            <li className="flex flex-col md:flex-row gap-6 whitespace-wrap w-full pt-6">
              <span className="text-grey-900 whitespace-nowrap font-semibold">
                Step 3
              </span>
              <div className="flex w-full flex-col gap-4">
                <span className="text-grey-700">
                  Enter the 6-digit code generated by your authentication app.
                </span>
                <form
                  onSubmit={handleSetup}
                  className="flex flex-col md:flex-row items-start justify-start md:justify-between gap-4">
                  <div className="flex flex-col w-full">
                    <Input
                      className={clsx(
                        'text-gray-600 shadow-none ring-none focus:!border-none w-full',
                        errors.mfaSetupToken &&
                          'border !border-error-300 rounded-lg'
                      )}
                      icon={errors.mfaSetupToken && <ErrorIcon />}
                      placeholder="123456"
                      pattern="[0-9]*"
                      inputMode="numeric"
                      maxLength={6}
                      value={mfaSetupToken}
                      {...register('mfaSetupToken')}
                      onChange={handleSetupCodeChange}
                      onInput={(e: any) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                      }}
                    />
                    {errors.mfaSetupToken && (
                      <span className="text-error-500 text-2sm mt-[6px]">
                        {errors.mfaSetupToken?.message?.toString()}
                      </span>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="px-3 py-2 bg-primary text-white text-base font-semibold rounded-md shadow-sm w-full md:w-auto items-start">
                    Enable
                  </Button>
                </form>
              </div>
            </li>
          </ol>
        </div>
      </div>
      <div className="hidden items-center lg:flex p-6">
        <div
          className="w-[280px] h-[280px]"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>
    </>
  );
};
export default Login;
